import { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Schedule } from '@material-ui/icons';
import AuditProvider from 'components/modules/service/Audit/AuditProvider';
import { ROWS_PER_PAGE } from 'consts';
import ExpansionList from '../../../../common/ExpansionList';
import { columnMeta } from './Scheduled/config/index';
import {
  useIsFspConfigUpdating,
  useIsProfileLoading,
} from '../../../implementation/Fsp/Detail/common';
import ExpandablePanel from '../../../../common/ExpandablePanel';
import GuestScheduledDetailsRender from './Scheduled/config/GuestScheduledDetailsRender';
import GuestCancelPaymentDialog from './Scheduled/config/GuestCancelPaymentDialog';
import GuestScheduledActionsRenderer from './Scheduled/config/GuestScheduledActionsRenderer';
import useGuestTransactionList from './useGuestTransactionList';
import {
  getFrequencyLabel,
  getStatusLabel,
  getTypeLabel,
} from './History/config/DetailsRender/consts';

const GUEST_SORT_IDS = {
  transactionType: 'transactionType',
  sendOn: 'sendOn',
  fundingAccount: 'fundingAccount',
  destination: 'destination',
  statusDerived: 'status',
  frequencyLabel: 'frequency',
  endDateDerived: 'endDateDerived',
  amount: 'amount',
  deliverOn: 'deliverOn',
};
const guestColumnMeta = columnMeta.map((column) => ({
  ...column,
  sortId: GUEST_SORT_IDS[column.id],
}));
const DATE_SORT_IDS = new Set(['sendOn', 'deliverOn']);
const SCHEDULED_QUERY_KEYS = {
  page: 'scheduledPage',
  rowsPerPage: 'scheduledSize',
  orderBy: 'scheduledOrderBy',
  order: 'scheduledOrder',
};

const getRowsPerPage = (value) => {
  const rowsPerPage = Number(value);
  return ROWS_PER_PAGE.includes(rowsPerPage) ? rowsPerPage : ROWS_PER_PAGE[0];
};

const getDateTimestamp = (item, key) => {
  const timestamp = item?.[key] ? new Date(item[key]).getTime() : NaN;
  return Number.isNaN(timestamp) ? null : timestamp;
};

const compareValues = (left, right, key) => {
  if (key === 'amount') {
    return (Number(left?.amount) || 0) - (Number(right?.amount) || 0);
  }
  if (key === 'transactionType') {
    return String(getTypeLabel(left?.transactionType) ?? '').localeCompare(
      String(getTypeLabel(right?.transactionType) ?? '')
    );
  }
  if (key === 'fundingAccount') {
    const fundingLabel = (item) =>
      [item?.fundingAccount, item?.fundingMask, item?.fundingAccountSuffix]
        .filter((value) => value !== undefined && value !== null)
        .join(' ');
    return fundingLabel(left).localeCompare(fundingLabel(right));
  }
  if (key === 'destination') {
    const destinationLabel = (item) =>
      [
        item?.destination,
        item?.coreAccountNumberMask,
        item?.destinationMask,
        item?.destinationAccountSuffix,
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(' ');
    return destinationLabel(left).localeCompare(destinationLabel(right));
  }
  if (key === 'status') {
    return String(
      getStatusLabel(left?.statusDerived ?? left?.status) ?? ''
    ).localeCompare(
      String(getStatusLabel(right?.statusDerived ?? right?.status) ?? '')
    );
  }
  if (key === 'frequency') {
    return String(
      getFrequencyLabel(left?.frequencyLabel ?? left?.frequency) ?? ''
    ).localeCompare(
      String(getFrequencyLabel(right?.frequencyLabel ?? right?.frequency) ?? '')
    );
  }
  if (key === 'endDateDerived') {
    return String(left?.endDateDerived ?? '').localeCompare(
      String(right?.endDateDerived ?? '')
    );
  }
  return getDateTimestamp(left, key) - getDateTimestamp(right, key);
};

function guestScheduledDetailsRender(transaction) {
  return <GuestScheduledDetailsRender transaction={transaction} />;
}

const GuestScheduledTransactionsPanel = ({
  items,
  fspId,
  loanNumber,
  onOpen,
  loading: transactionsLoading,
  error: transactionsError,
  totalRecords,
  authReady,
  status,
}) => {
  const profileLoading = useIsProfileLoading();
  const loading = !authReady || profileLoading || transactionsLoading;
  const updating = useIsFspConfigUpdating();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const {
    handleListChange,
    incomplete,
    listItems,
    listTotal,
    page,
    pageItems,
    rowsPerPage,
    sort,
  } = useGuestTransactionList({
    items,
    totalRecords,
    queryKeys: SCHEDULED_QUERY_KEYS,
    compareValues,
    dateSortIds: DATE_SORT_IDS,
    getDateTimestamp,
    normalizeRowsPerPage: getRowsPerPage,
  });
  const showList = authReady && (listItems.length > 0 || status === 'success');

  const handleRetry = () => {
    if (onOpen) onOpen(undefined, true);
  };

  const handleExpand = (expanded) => {
    if (expanded && onOpen) onOpen();
  };

  const handleCancelClick = useCallback((payment) => {
    setSelectedPayment(payment);
    setCancelDialogOpen(true);
  }, []);

  const handleCancelDialogClose = useCallback(() => {
    setCancelDialogOpen(false);
    setSelectedPayment(null);
  }, []);

  const handleCancelSuccess = useCallback(() => {
    setCancelDialogOpen(false);
    setSelectedPayment(null);
  }, []);

  const ActionsRenderer = useCallback(
    (props) => {
      return (
        <GuestScheduledActionsRenderer
          {...props}
          onCancelClick={handleCancelClick}
        />
      );
    },
    [handleCancelClick]
  );

  return (
    <AuditProvider>
      <>
        <ExpandablePanel
          fullHeight={false}
          icon={Schedule}
          id="guest-scheduled-transactions"
          showProgress={loading || updating}
          title="Scheduled Transactions"
          onChange={handleExpand}
        >
          {transactionsError && (
            <div role="alert">
              {transactionsError}
              <button type="button" onClick={handleRetry} disabled={!authReady}>
                Retry
              </button>
            </div>
          )}
          {!authReady && (
            <div role="status">
              Waiting for authentication before loading scheduled transactions.
            </div>
          )}
          {incomplete && (
            <div role="status">
              Some scheduled transactions are unavailable. Please retry the
              panel.
              <button type="button" onClick={handleRetry}>
                Retry
              </button>
            </div>
          )}
          {showList && (
            <ExpansionList
              key={`scheduled-${page}-${listItems.length}`}
              ActionsRenderer={ActionsRenderer}
              columnMeta={guestColumnMeta}
              detailsRender={guestScheduledDetailsRender}
              emptyTitle="No scheduled transactions found."
              data={pageItems}
              onChange={handleListChange}
              orderBy={sort.orderBy}
              order={sort.order}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE}
              total={listTotal}
            />
          )}
        </ExpandablePanel>
        <GuestCancelPaymentDialog
          open={cancelDialogOpen}
          payment={selectedPayment}
          fspId={fspId}
          loanNumber={loanNumber}
          onClose={handleCancelDialogClose}
          onCancelSuccess={handleCancelSuccess}
        />
      </>
    </AuditProvider>
  );
};

GuestScheduledTransactionsPanel.propTypes = {
  items: PropTypes.array,
  fspId: PropTypes.string,
  loanNumber: PropTypes.string,
  onOpen: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.string,
  totalRecords: PropTypes.number,
  authReady: PropTypes.bool,
  status: PropTypes.string,
};

GuestScheduledTransactionsPanel.defaultProps = {
  items: [],
  fspId: null,
  loanNumber: null,
  onOpen: undefined,
  loading: false,
  error: null,
  totalRecords: undefined,
  authReady: false,
  status: 'idle',
};

export default memo(GuestScheduledTransactionsPanel);
